import { useCallback, useEffect, useRef } from "react";
import { usePostHog } from "posthog-js/react";
import { IAccount } from "@/types";

type Properties = Record<string, unknown>;

function getPrimaryAccount(
  accounts: Record<string, IAccount> | undefined
): IAccount | null {
  if (!accounts) return null;
  const list = Object.values(accounts);
  return list.find((a) => a.primary) ?? list[0] ?? null;
}

export function useAnalytics(
  accounts?: Record<string, IAccount> | undefined
) {
  const posthog = usePostHog();
  const identifiedRef = useRef(false);

  // Session-resume: identify + alias when accounts arrive and we haven't identified yet
  useEffect(() => {
    if (identifiedRef.current || !accounts || !posthog) return;

    const primary = getPrimaryAccount(accounts);
    if (!primary) return;

    const count = Object.keys(accounts).length;

    posthog.identify(primary.id, {
      email: primary.email,
      name: primary.name,
      accounts_connected: count,
    });

    posthog.register({ accounts_connected: count });

    // Self-healing: alias secondary accounts so split persons get merged
    Object.values(accounts).forEach((account) => {
      if (account.id !== primary.id) {
        posthog.alias(account.id);
      }
    });

    identifiedRef.current = true;
  }, [accounts, posthog]);

  const identify = useCallback(
    (primaryAccountId: string, properties: Properties = {}) => {
      if (!posthog) return;
      posthog.identify(primaryAccountId, properties);

      const count =
        (properties.accounts_connected as number) ??
        (accounts ? Object.keys(accounts).length : 1);

      posthog.register({ accounts_connected: count });
      identifiedRef.current = true;
    },
    [posthog, accounts]
  );

  const track = useCallback(
    (event: string, properties?: Properties) => {
      posthog?.capture(event, properties);
    },
    [posthog]
  );

  const reset = useCallback(() => {
    posthog?.reset();
    identifiedRef.current = false;
  }, [posthog]);

  const setPersonProps = useCallback(
    (properties: Properties) => {
      posthog?.setPersonProperties(properties);
    },
    [posthog]
  );

  const updateSuperProperties = useCallback(
    (properties: Properties) => {
      posthog?.register(properties);
    },
    [posthog]
  );

  return { identify, track, reset, setPersonProps, updateSuperProperties };
}
